import { Injectable } from '@angular/core';
import { DashboardWithRelations as Dashboard } from '@osos/shared/api';
import {
  ApplicationSharingLevelEnum,
  DashboardElement,
  DashboardEntryPresenterModel,
  DashboardGroup,
  DashboardGroupPresenterModel,
} from '@osos/shared/types';
import sortBy from 'lodash-es/sortBy';
import { ParserBase } from '../../bases';

@Injectable({
  providedIn: 'root',
})
export class DashboardParserV1Service extends ParserBase<
  Dashboard,
  DashboardEntryPresenterModel
> {
  constructor() {
    super(Dashboard, DashboardEntryPresenterModel, 1);
  }

  getPresenterModel(model: Partial<Dashboard>): DashboardEntryPresenterModel {
    const presenter: DashboardEntryPresenterModel = {
      id: model.id,
      dataEntity: model.dataEntity,
      state: model.state,
      creationInfo: model.creationInfo,
      updateInfo: model.updateInfo,
      value: {
        id: model.id,
        name: model.value?.name,
        elements: this.convertFlatElementsToGrouped(model.value?.elements),
        relatedTo: model.value?.relatedTo,
      },
      specificationVersion: this.version,
    } as DashboardEntryPresenterModel;
    return presenter;
  }

  getDataModel(presenter: DashboardEntryPresenterModel): Dashboard {
    const model: Dashboard = {
      id: presenter.id,
      dataEntity: presenter.dataEntity,
      state: presenter.state,
      creationInfo: presenter.creationInfo,
      updateInfo: presenter.updateInfo,
      isInvalid: false,
      value: {
        id: presenter.value.id,
        name: presenter.value.name,
        elements: this.convertGroupedElementsToFlat(
          presenter.value.elements,
        ) as DashboardElement[],
        relatedTo: presenter?.value?.relatedTo || undefined,
        // dashboard for now alwasy public, this value is overriden by the backend
        sharing: {
          level: {
            id: ApplicationSharingLevelEnum.Public,
          },
          spaces: [],
        },
      },
      specificationVersion: this.version,
    };
    return model;
  }

  private createGroupElementPresenterModel(
    model: DashboardGroup,
  ): DashboardGroupPresenterModel {
    const presenter: DashboardGroupPresenterModel = {
      ...model,
      elements: [] as any[],
    };
    return presenter;
  }

  private createGroupElementDataModel(
    presenter: DashboardGroupPresenterModel,
  ): DashboardGroup {
    const model: DashboardGroup = {
      id: presenter.id,
      description: presenter?.description,
      name: presenter.name,
      elementType: presenter.elementType,
      icon: presenter.icon,
      index: presenter.index,
    };
    return model;
  }
  /** Flatten Grouped elements **/
  private convertGroupedElementsToFlat(elements: any[]) {
    const flatElements = [];
    //Get the root elements to the flat
    elements.forEach((e) => {
      const newElement = this._isGroupElement(e)
        ? this.createGroupElementDataModel(e)
        : e;
      flatElements.push(newElement);
      this.insertChildrenIntoFlatElements(e, flatElements);
    });
    return flatElements;
  }

  private insertChildrenIntoFlatElements(e, flatElements) {
    if (!e?.elements?.length) return;
    e.elements.forEach((child) => {
      const newElement = this._isGroupElement(child)
        ? this.createGroupElementDataModel(child)
        : child;
      flatElements.push(newElement);
      this.insertChildrenIntoFlatElements(child, flatElements);
    });
  }

  /** Group elements **/
  private convertFlatElementsToGrouped(elements: any[] = []) {
    let groupedElements = [];
    //Get the root elements
    elements.forEach((e) => {
      if (!e?.group) {
        const newElement = this._isGroupElement(e)
          ? this.createGroupElementPresenterModel(e)
          : e;
        groupedElements.push(newElement);
      }
    });
    groupedElements = sortBy(groupedElements, 'index');
    //Get the rest nested elements
    groupedElements.forEach((e) => {
      if (this._isGroupElement(e)) this.getChildren(elements, e);
    });
    return groupedElements;
  }

  private getChildren(elements: any[], e) {
    e.elements = this._getChildren(elements, e);
    if (!e.elements.length) return;
    e.elements.forEach((child) => {
      if (this._isGroupElement(e)) {
        this.getChildren(elements, child);
      }
    });
  }

  private _getChildren(elements: any[], parent) {
    const children = [];
    elements.forEach((e) => {
      if (parent.id && e?.group === parent.id) {
        const newElement = this._isGroupElement(e)
          ? this.createGroupElementPresenterModel(e)
          : e;
        children.push(newElement);
      }
    });
    return children;
  }

  private _isGroupElement(e) {
    return e.elementType === 'DashboardGroup';
  }
}
